import React, { useEffect, useState } from "react";
import { Button, DatePicker, message, Space, Table, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import { IntegrationService } from "../../Services/IntegrationService";
import { formatDateString } from "../../Helpers/DateHelpers";
import { WebApiIntegrationsDtoPingPaymentPaymentReportPaymentDto } from "../../Repository/eventbookyapi";
import LayoutLeftMenu from "../../Components/Layout/LayoutLeftMenu";
import LeftMenuSettings from "../Account/LeftSettings";
import { TextService } from "../../Services/TextService";

const PingPaymentPaymentReport: React.FC = () => {
    const [data, setData] = useState<WebApiIntegrationsDtoPingPaymentPaymentReportPaymentDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const GetText = TextService.GetText;

    const fetchAllocations = async (from?: Dayjs | null, to?: Dayjs | null) => {
        setLoading(true);
        try {
            const fromStr = from ? from.format("YYYY-MM-DD") : undefined;
            const toStr = to ? to.format("YYYY-MM-DD") : undefined;
            const result = await IntegrationService.GetAllocations(fromStr, toStr);
            setData(result.payments ?? []);
        } finally {
            setLoading(false);
        }
    };

    const downloadPdf = async () => {
        setPdfLoading(true);
        try {
            const fromStr = fromDate ? fromDate.format("YYYY-MM-DD") : undefined;
            const toStr = toDate ? toDate.format("YYYY-MM-DD") : undefined;
            const blob = await IntegrationService.DownloadAllocationsPdf(fromStr, toStr);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `PingPayment_Allocations_${new Date().toISOString().slice(0, 10)}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch {
            message.error("Kunde inte ladda ner PDF");
        } finally {
            setPdfLoading(false);
        }
    };

    const handleFromDateChange = (date: Dayjs | null) => {
        setFromDate(date);
    };

    const handleToDateChange = (date: Dayjs | null) => {
        setToDate(date);
    };

    const applyFilter = () => {
        fetchAllocations(fromDate, toDate);
    };

    useEffect(() => {
        fetchAllocations();
    }, []);

    const columns: ColumnsType<WebApiIntegrationsDtoPingPaymentPaymentReportPaymentDto> = [
        {
            title: GetText("PingPaymentReport_ColumnReference"),
            dataIndex: "reference",
            key: "reference",
            width: 120,
            render: (v) => v ?? "-",
        },
        {
            title: GetText("PingPaymentReport_ColumnMemberUniqueId"),
            dataIndex: "memberUniqueId",
            key: "memberUniqueId",
            width: 120,
            render: (v) => v ?? "-",
        },
        {
            title: GetText("PingPaymentReport_ColumnFirstName"),
            dataIndex: "firstName",
            key: "firstName",
            render: (v) => v ?? "-",
        },
        {
            title: GetText("PingPaymentReport_ColumnLastName"),
            dataIndex: "lastName",
            key: "lastName",
            render: (v) => v ?? "-",
        },
        {
            title: GetText("PingPaymentReport_ColumnOrginalAmount"),
            dataIndex: "orginalAmount",
            key: "orginalAmount",
            render: (v) => (v !== undefined ? v : "-"),
        },
        {
            title: GetText("PingPaymentReport_ColumnAmount"),
            dataIndex: "amount",
            key: "amount",
            render: (v) => (v !== undefined ? v : "-"),
        },
        {
            title: GetText("PingPaymentReport_ColumnFee"),
            key: "fee",
            render: (_: any, record: WebApiIntegrationsDtoPingPaymentPaymentReportPaymentDto) => {
                const fee = (record.orginalAmount ?? 0) - (record.amount ?? 0);
                return fee !== 0 ? fee.toFixed(2) : "-";
            },
        },
        {
            title: GetText("PingPaymentReport_ColumnSettledAt"),
            dataIndex: "settledAt",
            key: "settledAt",
            render: (v) => formatDateString(v) || "-",
        },
        {
            title: GetText("PingPaymentReport_ColumnDisbursedAt"),
            dataIndex: "disbursedAt",
            key: "disbursedAt",
            render: (v) => formatDateString(v) || "-",
        },
        {
            title: GetText("PingPaymentReport_ColumnBankAccountNumber"),
            dataIndex: "bankAccountNumber",
            key: "bankAccountNumber",
            render: (v) => v ?? "-",
        },
    ];

    return (
        <LayoutLeftMenu title={GetText("PingPaymentReport_Title")} menu={<LeftMenuSettings selected={"integrations"} />}>
            <p>
                {GetText("PingPaymentReport_Description")}{" "}
                <Tooltip title={GetText("PingPaymentReport_InfoTooltip")}>
                    <InfoCircleOutlined style={{ color: "#1890ff", cursor: "help" }} />
                </Tooltip>
            </p>
            <Space style={{ marginBottom: 16 }}>
                <DatePicker
                    value={fromDate}
                    onChange={handleFromDateChange}
                    placeholder={GetText("PingPaymentReport_FromDate")}
                    allowClear
                />
                <DatePicker
                    value={toDate}
                    onChange={handleToDateChange}
                    placeholder={GetText("PingPaymentReport_ToDate")}
                    allowClear
                />
                <Button type="primary" onClick={applyFilter} loading={loading}>
                    {GetText("PingPaymentReport_Filter")}
                </Button>
                <Button onClick={downloadPdf} loading={pdfLoading}>
                    {GetText("PingPaymentReport_DownloadPdf")}
                </Button>
            </Space>
            <Table
                rowKey="memberId"
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{ pageSize: 20 }}
            />
        </LayoutLeftMenu>
    );
};

export default PingPaymentPaymentReport;
